import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiGet } from "./api.js";

/**
 * Sprint 29 Wave A — feature-flag client for the customer web app.
 * Mirrors the Sprint 26 / 27 / 28 hooks. Reads
 * `GET /v1/sprint29-feature-flags`.
 */

export type Sprint29FlagKey = string;

export interface Sprint29FlagSnapshot {
  readonly key: string;
  readonly wave: string;
  readonly visibility: "public_testable" | "internal_admin_only" | "hidden_disabled";
  readonly enabled: boolean;
  readonly visible: boolean;
  readonly source: "default" | "env";
  readonly description: string;
}

export interface Sprint29FeatureFlagsResponse {
  readonly sprint: 29;
  readonly resolvedAt: string;
  readonly viewer: {
    readonly roles: readonly string[];
    readonly anonymous: boolean;
  };
  readonly flags: ReadonlyArray<Sprint29FlagSnapshot>;
}

export const SPRINT29_FEATURE_FLAGS_KEY = ["feature-flags", "sprint29"] as const;

export function useSprint29FeatureFlags() {
  return useQuery<Sprint29FeatureFlagsResponse, Error>({
    queryKey: SPRINT29_FEATURE_FLAGS_KEY,
    queryFn: ({ signal }) =>
      apiGet<Sprint29FeatureFlagsResponse>("/v1/sprint29-feature-flags", undefined, signal),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useSprint29FlagVisible(key: string): {
  visible: boolean;
  loading: boolean;
} {
  const { data, isLoading } = useSprint29FeatureFlags();
  return useMemo(() => {
    if (!data) return { visible: false, loading: isLoading };
    const flags = Array.isArray(data.flags) ? data.flags : [];
    const f = flags.find((flag) => flag.key === key);
    return { visible: f?.visible === true, loading: false };
  }, [data, isLoading, key]);
}

export function useSprint29FlagSnapshot(key: string): Sprint29FlagSnapshot | undefined {
  const { data } = useSprint29FeatureFlags();
  return data?.flags.find((f) => f.key === key);
}

/**
 * GT-STATUS-SOURCE-OF-TRUTH-LIVEMAP-FALLBACK-…-03 — fail-OPEN variant
 * (mirror of `useSprint28FlagVisibleDefaultOn`). Returns `visible:true`
 * while flags are loading/failed so the Live Map's current shell
 * (toolbar, Data tab) does not silently revert to the legacy header on a
 * transient flag-fetch error. Use ONLY for default-ON flags; an explicit
 * loaded `visible:false` is still honoured.
 */
export function useSprint29FlagVisibleDefaultOn(key: string): { visible: boolean } {
  const snap = useSprint29FlagSnapshot(key);
  return { visible: snap ? snap.visible : true };
}
